// Kyle's daily work queue: who did we quote, and who never came back.
//
// This tab used to render the CUSTOMERS LIST — "revival" wasn't in
// CustomersPage's reservedRoutes, so the router read it as a customer id
// and mounted CustomerList + a detail page fetching customer "revival"
// (that was the "Not found." overlay).
//
// Filters/sort come from the SAME backend source as the outreach composer
// (quote_status / quote_sent_from|to → apply_quote_status_filter,
// ordering=latest_quote_date), so what Kyle sees here is exactly what a
// segment would send to.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/client';
import SegmentComposer from '../outreach/SegmentComposer';
import { CUSTOMERS_AUDIENCE } from '../outreach/audiences';
import './QuotesQueue.css';

const CHIPS = [
  { key: 'sent', label: 'Sent, no answer' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'declined', label: 'Declined' },
  { key: 'expired', label: 'Expired (30d+)' },
  { key: 'no_quote', label: 'No quote' },
  { key: '', label: 'All' },
];

const fmtMoney = (v) =>
  v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const fmtDate = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
};

const daysSince = (v) => {
  if (!v) return null;
  try {
    return Math.floor((Date.now() - new Date(v).getTime()) / 86400000);
  } catch { return null; }
};

export default function QuotesQueue() {
  const navigate = useNavigate();
  // Default = Kyle's queue: quoted, never accepted, oldest first.
  const [status, setStatus] = useState('sent');
  const [ordering, setOrdering] = useState('latest_quote_date');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Visual selection only. "Outreach these" sends the FILTER (the query), not
  // these ids — a snapshot goes stale between click and send and skips the
  // send-time exclusion re-check. The checkboxes are for Kyle to eyeball the
  // set; the segment that ships is quote_status.
  const [checked, setChecked] = useState(() => new Set());
  const [outreachOpen, setOutreachOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ordering, page_size: 200 };
      if (status) params.quote_status = status;
      if (search.trim()) params.q = search.trim();
      const r = await api.get('/customers/', { params });
      const data = r.data?.results || (Array.isArray(r.data) ? r.data : []);
      setRows(data);
    } catch {
      toast.error('Could not load quotes');
    } finally {
      setLoading(false);
    }
  }, [status, ordering, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const withQuotes = useMemo(
    () => rows.filter((r) => status === 'no_quote' ? !r.latest_quote_status : true),
    [rows, status]
  );

  // Selection is per-filter — changing the chip clears it.
  useEffect(() => { setChecked(new Set()); }, [status]);

  const allChecked = withQuotes.length > 0 && withQuotes.every((r) => checked.has(r.id));
  const toggleAll = () =>
    setChecked(allChecked ? new Set() : new Set(withQuotes.map((r) => r.id)));
  const toggleOne = (id) =>
    setChecked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // "Outreach these" ships the FILTER, not ids: the composer opens on the same
  // quote_status Kyle is viewing and re-resolves (+ re-checks exclusions) at
  // send time. The current chip (e.g. 'sent' = Sent, no answer) is the segment.
  const currentChipLabel = (CHIPS.find((c) => c.key === status) || {}).label || 'these';
  const openOutreach = () => setOutreachOpen(true);

  const toggleSort = (field) =>
    setOrdering((cur) => (cur === field ? `-${field}` : field));

  return (
    <div className="qq">
      <div className="qq-head">
        <div>
          <h2>Quotes</h2>
          <p className="qq-sub">
            Who you quoted and never heard back from. Same filters the outreach
            composer uses — what you see here is what a follow-up would reach.
          </p>
        </div>
        <input
          className="qq-search"
          placeholder="Search name, phone, email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="qq-chips">
        {CHIPS.map((c) => (
          <button
            key={c.key || 'all'}
            className={`qq-chip ${status === c.key ? 'on' : ''}`}
            onClick={() => setStatus(c.key)}
          >
            {c.label}
          </button>
        ))}
        <span className="qq-count">
          {loading ? 'loading…' : `${withQuotes.length} ${withQuotes.length === 1 ? 'record' : 'records'}`}
        </span>
        <button
          className="qq-outreach"
          disabled={loading || withQuotes.length === 0}
          title={`Open the outreach composer on this exact filter (${currentChipLabel}). Sends the segment, re-checked at send time — not a snapshot of the rows shown.`}
          onClick={openOutreach}
        >
          Outreach these → <span className="qq-outreach-n">{withQuotes.length} in “{currentChipLabel}”</span>
        </button>
      </div>

      <div className="qq-table-wrap">
        <table className="qq-table">
          <thead>
            <tr>
              <th className="qq-check">
                <input type="checkbox" checked={allChecked} onChange={toggleAll}
                  title="Select all in this filter" />
              </th>
              <th>Customer</th>
              <th>Phone</th>
              <th>Service</th>
              <th className="num">Amount</th>
              <th>
                <button className="qq-sort" onClick={() => toggleSort('latest_quote_date')}>
                  Quote date {ordering === 'latest_quote_date' ? '↑' : ordering === '-latest_quote_date' ? '↓' : ''}
                </button>
              </th>
              <th>Age</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {!loading && withQuotes.length === 0 && (
              <tr><td colSpan={8} className="qq-empty">No quotes match.</td></tr>
            )}
            {withQuotes.map((r) => {
              const age = daysSince(r.latest_quote_date);
              return (
                <tr key={r.id} className="qq-row" onClick={() => navigate(`/customers/${r.id}`)}>
                  <td className="qq-check" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={checked.has(r.id)}
                      onChange={() => toggleOne(r.id)} />
                  </td>
                  <td className="qq-name">{r.full_name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || '(no name)'}</td>
                  <td>{r.primary_phone || '—'}</td>
                  <td>{r.latest_quote_service || '—'}</td>
                  <td className="num">{fmtMoney(r.latest_quote_total)}</td>
                  <td>{fmtDate(r.latest_quote_date)}</td>
                  <td>{age == null ? '—' : `${age}d`}</td>
                  <td>
                    <span className={`qq-status ${r.latest_quote_status || 'none'}`}>
                      {r.latest_quote_status || 'no quote'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Third entry point into the ONE shared composer (Leads · Customers ·
          Quotes). We pass the FILTER (quote_status), not row ids — the segment
          re-resolves and re-checks exclusions at send time. */}
      <SegmentComposer
        open={outreachOpen}
        onClose={() => setOutreachOpen(false)}
        audience={CUSTOMERS_AUDIENCE}
        presetFilters={{ quote_status: status || undefined }}
      />
    </div>
  );
}
